import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { AplicacionRecipienteResponse } from "../../../../../modulos/aplicaciones/response/AplicacionRecipienteResponse";
import { aplicacionRecipienteDeleteGrpcService } from "../../../../../modulos/aplicaciones/services/AplicacionRecipienteDeleteGrpcService";
import { fx_createAplicacionRecipienteGrpcService } from "../../../../../modulos/aplicaciones/services/AplicacionRecipienteGrpcService";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { ConfirmModalRender } from "../../../../implementations/modal/ConfirmModalRender";
import { RecipienteFormModal } from "../modal/RecipienteFormModal";
import rowsTemplate from "./recipienteRowTemplate.html";
import template from "./recipientesTemplate.html";

mustache.parse(template);
mustache.parse(rowsTemplate);

/** Título con el que se agrupan los avisos de esta sección en los toasts. */
const TOAST_TITULO: string = "Recipientes";

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let RECIPIENTES_ID: number = 0;

/** Sección "Recipientes de aplicación": las canecas y tanques con los que se
 *  mide una aplicación manual, cada uno con su equivalencia en litros.
 *
 *  El alcance es el **cliente**, no la finca activa: son equipo físico que se
 *  mueve entre fincas.
 *
 *  **Todas las filas se editan y se eliminan.** La lista no arranca vacía porque
 *  el server le siembra al cliente sus copias de los dos tamaños estándar la
 *  primera vez —sin ningún recipiente no podría registrar una aplicación
 *  manual—, pero son copias suyas, no registros compartidos: ajustar la caneca a
 *  los 180 L que de verdad usa la finca es lo primero que se necesita hacer acá.
 *  El 2026-10-14 esos dos eran globales y de solo lectura; se descartó al
 *  día siguiente por eso mismo. */
export class RecipientesRender implements IConnectRender {
  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number = ++RECIPIENTES_ID;

  /** Cliente dueño de los recipientes (viene de la finca activa). */
  private readonly _clienteId: string;

  /** Recipientes actualmente listados. */
  private _recipientes: AplicacionRecipienteResponse[] = [];

  constructor() {
    this._clienteId =
      serviceProvider.getFincaSelectedService().getRequired().clienteId;
  }

  /** HTML de la card; las filas se inyectan al terminar la carga. */
  render(): string {
    return mustache.render(template, { id: this._id });
  }

  /** Resuelve los elementos, engancha la delegación de clicks y carga la lista. */
  connect(): void {
    this._card = document.getElementById(`mcRecipientes-${this._id}`);
    this._rows = document.getElementById(`mcRecipienteRows-${this._id}`);

    this._card?.addEventListener("click", this._onClick);

    this._cargar();
  }

  dispose(): void {
    this._disposed = true;
    this._card?.removeEventListener("click", this._onClick);
    this._formModal?.dispose();
    this._formModal = null;
  }

  /** Elemento raíz de la card (delegación de eventos). */
  private _card: HTMLElement | null = null;
  /** Contenedor de las filas. */
  private _rows: HTMLElement | null = null;
  /** Modal de alta/edición abierto, si lo hay (se cierra al desmontar). */
  private _formModal: RecipienteFormModal | null = null;

  /** Marca de componente desmontado: nada debe tocar el DOM después. */
  private _disposed: boolean = false;

  /** Carga los recipientes del cliente y pinta la lista. */
  private _cargar(): void {
    fx_createAplicacionRecipienteGrpcService(this._clienteId)
      .getListAsync()
      .then((recipientes) => {
        if (this._disposed) return;
        this._recipientes = recipientes;
        this._pintarLista();
      })
      .catch((error) => {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("Error al cargar los recipientes", error);
      });
  }

  /** Rearma las filas con el estado actual. */
  private _pintarLista(): void {
    if (this._disposed || !this._rows) return;

    this._rows.innerHTML = mustache.render(rowsTemplate, {
      recipientes: this._recipientes.map((recipiente) => ({
        id: recipiente.id,
        nombre: recipiente.nombre,
        // `es-CO` y sin decimales forzados: 200 se lee "200 L" y 208,2 conserva
        // su coma, que es la que hace exacto el factor de conversión.
        litrosTexto: recipiente.litros.toLocaleString("es-CO", {
          maximumFractionDigits: 2,
        }),
      })),
    });
  }

  /** Delegación de clicks: botón de la card y botones de cada fila. */
  private _onClick = (e: MouseEvent): void => {
    const elemento = (e.target as HTMLElement).closest<HTMLElement>(
      "[data-accion]",
    );
    if (!elemento) return;

    switch (elemento.dataset.accion) {
      case "agregar":
        this._abrirFormulario(null);
        return;
      case "editar":
        this._abrirFormulario(this._recipienteDe(elemento));
        return;
      case "eliminar":
        this._confirmarEliminar(this._recipienteDe(elemento));
        return;
    }
  };

  /** Abre el modal de alta (`null`) o de edición. */
  private _abrirFormulario(
    recipiente: AplicacionRecipienteResponse | null,
  ): void {
    this._formModal = new RecipienteFormModal(
      this._clienteId,
      recipiente,
      (guardado) => this._onGuardado(guardado),
    );
    this._formModal.init();
  }

  /** Suma o reemplaza el recipiente guardado, reordena la lista y avisa. */
  private _onGuardado(guardado: AplicacionRecipienteResponse): void {
    this._formModal = null;
    if (this._disposed) return;

    const existe = this._recipientes.some((r) => r.id === guardado.id);
    this._recipientes = existe
      ? this._recipientes.map((r) => (r.id === guardado.id ? guardado : r))
      : [...this._recipientes, guardado];
    // Mismo orden que el servicio: por litros y después por nombre, para que la
    // lista no salte de posición respecto de lo que muestra el formulario.
    this._recipientes.sort(
      (a, b) => a.litros - b.litros || a.nombre.localeCompare(b.nombre),
    );

    this._pintarLista();
    serviceProvider
      .getErrorService()
      .addSuccess(
        TOAST_TITULO,
        `${guardado.nombre} quedó guardado (${guardado.litros} L).`,
      );
  }

  /** Pide confirmación y elimina. */
  private _confirmarEliminar(
    recipiente: AplicacionRecipienteResponse | null,
  ): void {
    if (!recipiente) return;

    new ConfirmModalRender({
      title: "Eliminar recipiente",
      message: `¿Eliminar <strong>${recipiente.nombre}</strong>? Las aplicaciones ya registradas con él no se modifican.`,
      onConfirm: () => this._eliminarAsync(recipiente),
    }).init();
  }

  /** Elimina el recipiente y lo quita de la lista. Devuelve la promesa **sin
   *  atrapar el error** a propósito: `ConfirmModalRender` lo necesita para
   *  avisar y dejar su modal abierto (si se atrapara acá, el modal se cerraría
   *  como si hubiera salido bien). */
  private _eliminarAsync(
    recipiente: AplicacionRecipienteResponse,
  ): Promise<void> {
    return aplicacionRecipienteDeleteGrpcService
      .deleteAsync(recipiente)
      .then(() => {
        if (this._disposed) return;
        this._recipientes = this._recipientes.filter(
          (r) => r.id !== recipiente.id,
        );
        this._pintarLista();
        serviceProvider
          .getErrorService()
          .addSuccess(TOAST_TITULO, `Se eliminó ${recipiente.nombre}.`);
      });
  }

  /** Recipiente al que pertenece la fila que contiene al elemento. */
  private _recipienteDe(
    elemento: HTMLElement,
  ): AplicacionRecipienteResponse | null {
    const id = elemento.closest<HTMLElement>(".sx-list-row")?.dataset.id;
    if (!id) return null;
    return this._recipientes.find((r) => r.id === id) ?? null;
  }
}
